import {
  type Color,
  DUCK,
  MASKED,
  type MoveEvent,
  type MoveRecord,
  type Position,
  SNAIL,
  TYPE_MASK,
  WALL,
  WHITE,
  baseType,
  isNeutral,
  pieceColor,
  pieceDef,
} from '@chessbut/engine';
import type { SoundName } from '../lib/sound.ts';
import { bounceUi } from './bounce.ts';
import { classicUi } from './classic.ts';
import { comebackUi } from './comeback.ts';
import { cooldownUi } from './cooldown.ts';
import { duelUi } from './duel.ts';
import { explodeUi } from './explode.ts';
import { flipUi } from './flip.ts';
import { gooseUi } from './goose.ts';
import { greyUi } from './grey.ts';
import { heirUi } from './heir.ts';
import { hpUi } from './hp.ts';
import { lavaUi } from './lava.ts';
import { levelupUi } from './levelup.ts';
import { masqueradeUi } from './masquerade.ts';
import { portalUi } from './portal.ts';
import { snailUi } from './snail.ts';
import { stormUi } from './storm.ts';
import { traitorUi } from './traitor.ts';
import { wildcardUi } from './wildcard.ts';
import { wrapUi } from './wrap.ts';
import { zombieUi } from './zombie.ts';

/** Something drawn on the board besides pieces. `hint` ones hide when hints are off. */
export type Decoration =
  | { kind: 'arrow'; from: number; to: number; tone: 'light' | 'dark' | 'danger'; hint?: boolean }
  | { kind: 'mark'; sq: number; tone: 'danger' | 'doomed' | 'info'; hint?: boolean }
  | { kind: 'badge'; sq: number; text: string; tone: 'normal' | 'danger'; hint?: boolean }
  /** Art drawn on the square itself, under any piece (portals, graves). */
  | { kind: 'tile'; sq: number; image: string; title?: string; hint?: boolean };

export interface PlayerNote {
  icon?: string;
  text: string;
  tone?: 'danger';
  title?: string;
}

export interface MoveBadge {
  icon: string;
  victim?: number;
  title: string;
}

/** The variant's live state under the board ("Snails crawl in 2", "Your knight is lunch next move"). */
export interface TwistState {
  text: string;
  /** Countdown pips: `on` filled out of `total`, with a tooltip. */
  pips?: { on: number; total: number; title?: string };
  alert?: boolean;
}

/** One cell of the 3×3 "rules peek" mini board on the Today card. */
export interface PeekCell {
  image?: string;
  threat?: boolean;
}

/** A button for an action move that isn't made on a board ("Challenge!", "Ready", "Pass"). */
export interface ActionButton {
  label: string;
  move: number;
  tone?: 'strong' | 'danger';
  title?: string;
}

/** A small second board drawn under the main one (the Pit, the duel board). */
export interface SideBoardSpec {
  title: string;
  /** One line under the title ("Win the duel to move"). */
  hint?: string;
  cols: number;
  rows: number;
  /** Piece codes, row 0 (White's side) first. */
  cells: ArrayLike<number>;
  /** The cell an action's from/to value names, or -1. */
  cellOf(value: number): number;
  /** Whether a legal action is a move on this board. */
  isMove(move: number): boolean;
  /** Text for each choice when one cell-to-cell move has several versions ("Back on g1"). */
  optionLabel?(move: number): string;
  /** The board is in play (glows): it's where the next move happens. */
  active?: boolean;
}

/** Optional UI hooks a variant can provide. Everything falls back to plain chess. */
export interface VariantUi {
  /** Action moves (FLAG_ACTION) made on the main board like a move (default: none are). */
  boardAction?(move: number): boolean;
  /** Buttons for the other actions. */
  actions?(pos: Position, legal: number[]): ActionButton[];
  sideBoard?(pos: Position): SideBoardSpec | null;
  /**
   * What `viewer` gets to see (hidden pieces swapped for MASKED). Null viewer: everything
   * (the game is over). Hooks below get the shown position.
   */
  view?(pos: Position, viewer: Color | null): Position;
  /** Pass & play needs a "pass the device" screen between turns. */
  secret?: boolean;
  /** Don't count material in the player bars (it would give hidden pieces away). */
  hideMaterial?: boolean;
  /** Extra class on the board element ("grey" draws every piece grey). */
  boardClass?: string;
  pieceImage?(pos: Position, sq: number, piece: number): string | null;
  /** What screen readers call a piece ("goose", "white's snail"); default "white knight". */
  pieceLabel?(pos: Position, sq: number, piece: number): string | null;
  decorations?(pos: Position): Decoration[];
  playerNote?(pos: Position, color: Color): PlayerNote | null;
  /** Status line when the side to move faces the variant's threat (not check). */
  threatText?(you: boolean, name: string): string;
  /** The twist bar; `you` is the viewer's colour (null for spectators/pass & play). */
  twist?(pos: Position, you: Color | null): TwistState | null;
  eventSound?(e: MoveEvent): SoundName | null;
  moveBadge?(rec: MoveRecord): MoveBadge | null;
  /** Custom text for a move in the move list (default: SAN). */
  san?(rec: MoveRecord): string | null;
  /** Leave a move out of the move list (duel moves, secret setup swaps). */
  hideInMoveList?(rec: MoveRecord): boolean;
  /** Big illustration for heroes and end cards. */
  heroImage?: string;
  /** 3×3 crop that shows the twist at a glance (row by row from the top-left). */
  peek?: PeekCell[];
}

const UIS: Record<string, VariantUi> = {
  snail: snailUi,
  classic: classicUi,
  explode: explodeUi,
  goose: gooseUi,
  bounce: bounceUi,
  storm: stormUi,
  cooldown: cooldownUi,
  hp: hpUi,
  lava: lavaUi,
  levelup: levelupUi,
  wrap: wrapUi,
  zombie: zombieUi,
  portal: portalUi,
  traitor: traitorUi,
  flip: flipUi,
  duel: duelUi,
  wildcard: wildcardUi,
  masquerade: masqueradeUi,
  grey: greyUi,
  comeback: comebackUi,
  heir: heirUi,
};

export function variantUi(id: string): VariantUi {
  return UIS[id] ?? {};
}

const NEUTRAL_ART: Record<number, string> = { [SNAIL]: '/art/snail-w.svg', [DUCK]: '/art/goose.svg', [WALL]: '/art/wall.svg' };

/** Default art for a piece code (colour + letter). */
export function pieceImage(piece: number): string {
  const type = piece & TYPE_MASK;
  if (isNeutral(piece)) return NEUTRAL_ART[type] ?? '/art/wall.svg';
  const color = pieceColor(piece) === WHITE ? 'w' : 'b';
  if (type === MASKED) return `/art/mask-${color}.svg`;
  return `/art/${color}${pieceDef(baseType(type)).letter.toUpperCase()}.svg`;
}

export function imageFor(ui: VariantUi, pos: Position, sq: number, piece: number): string {
  return ui.pieceImage?.(pos, sq, piece) ?? pieceImage(piece);
}

export function labelFor(ui: VariantUi, pos: Position, sq: number, piece: number): string {
  const custom = ui.pieceLabel?.(pos, sq, piece);
  if (custom) return custom;
  const name = pieceDef(piece & TYPE_MASK).name;
  const color = pieceColor(piece);
  return color === -1 ? name : `${color === WHITE ? 'white' : 'black'} ${name}`;
}
